/**
 * Dashboard — broker-centric overview matching the approved mockup.
 *
 * Layout (top-to-bottom):
 *   1. Greeting header (dynamic sun/moon/moon-star by time of day)
 *   2. 6 KPI tiles (New Leads, Follow-ups, Awaiting Docs, Docs Received, Approved, Settled)
 *   3. Pipeline Overview (5-stage chevron) + Settlements chart (6-month bar)
 *   4. My Tasks + Recent Activity + Automations
 *   5. Top Lenders + My Performance (MTD) + Quick Actions
 *
 * All data is real from the API. Widgets show empty states when there's
 * no data (not fake demo numbers).
 */
import { Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  Users,
  CheckSquare,
  Send,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  FileText,
  Clock,
  Loader2,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  ChevronRight,
  DollarSign,
  Plus,
  UserPlus,
  Zap,
  Award,
  BarChart3,
  Target,
  FileCheck,
  Handshake,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { LenderLogo } from "@/components/LenderLogo"
import { dashboardApi, type PipelineStage } from "@/lib/api"
import { timeAgo } from "@shared/utils/date"
import { useAuth } from "@/hooks/useAuth"
import { cn } from "@/lib/utils"

// ============================================================
// Constants
// ============================================================

const OVERVIEW_STAGES: PipelineStage[] = [
  "new_lead",
  "awaiting_docs",
  "docs_received",
  "approved",
  "settled",
]

const STAGE_LABEL: Record<PipelineStage, string> = {
  new_lead: "New Lead",
  day_one: "Day 1",
  day_two: "Day 2",
  day_three: "Day 3",
  general_followup: "General Follow-up",
  awaiting_docs: "Awaiting Docs",
  docs_received: "Docs Received",
  approved: "Approved",
  settled: "Settled",
  closed: "Closed",
}

// Pipeline chevron gradient — cool blue → warm green as deals progress
const STAGE_GRADIENT: Record<PipelineStage, string> = {
  new_lead: "from-blue-500 to-blue-600",
  day_one: "from-blue-400 to-blue-500",
  day_two: "from-indigo-400 to-indigo-500",
  day_three: "from-indigo-500 to-indigo-600",
  general_followup: "from-amber-400 to-amber-500",
  awaiting_docs: "from-violet-500 to-violet-600",
  docs_received: "from-cyan-500 to-cyan-600",
  approved: "from-emerald-500 to-emerald-600",
  settled: "from-emerald-600 to-teal-600",
  closed: "from-slate-500 to-slate-600",
}

const ACTION_LABEL: Record<string, string> = {
  contact_created: "New contact",
  contact_updated: "Updated contact",
  contact_deleted: "Deleted contact",
  contact_assigned: "Reassigned contact",
  deal_created: "New deal",
  deal_updated: "Updated deal",
  deal_stage_changed: "Deal stage changed",
  deal_deleted: "Deleted deal",
  task_created: "New task",
  task_updated: "Updated task",
  task_completed: "Task completed",
  task_deleted: "Deleted task",
  automation_created: "Automation created",
  automation_activated: "Automation activated",
  automation_paused: "Automation paused",
  automation_manual_enrol: "Enrolled in automation",
  automation_run_cancelled: "Enrolment removed",
  automation_runs_bulk_cancelled: "Bulk enrolments removed",
  aircall_sms_sent: "SMS sent",
  login_ok: "Signed in",
  logout: "Signed out",
}

const ACTION_ICON: Record<string, typeof Users> = {
  contact_created: UserPlus,
  contact_updated: Users,
  deal_created: FileText,
  deal_stage_changed: TrendingUp,
  task_created: CheckSquare,
  task_completed: CheckCircle2,
  automation_created: Zap,
  automation_activated: Zap,
  automation_manual_enrol: Zap,
  aircall_sms_sent: Send,
  login_ok: CheckCircle2,
}

// ============================================================
// Helpers
// ============================================================

// Brisbane wall-clock hour (0-23). All dashboard greetings + date badges
// are relative to Brisbane time so a broker travelling still sees "Good
// morning" when it's morning in Brisbane, not in their overseas tz.
function brisbaneHour(): number {
  const parts = new Intl.DateTimeFormat("en-AU", {
    hour: "2-digit",
    hour12: false,
    timeZone: "Australia/Brisbane",
  }).formatToParts(new Date())
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0")
  return h === 24 ? 0 : h
}

function getTimeOfDay(): { greeting: string; Icon: typeof Sun } {
  const h = brisbaneHour()
  if (h < 6) return { greeting: "Good evening", Icon: Moon }
  if (h < 12) return { greeting: "Good morning", Icon: Sunrise }
  if (h < 18) return { greeting: "Good afternoon", Icon: Sun }
  if (h < 21) return { greeting: "Good evening", Icon: Sunset }
  return { greeting: "Good evening", Icon: Moon }
}

function formatDateHuman(): string {
  return new Date().toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Australia/Brisbane",
  })
}

function formatShortCurrency(n: number): string {
  if (n === 0) return "$0"
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`
  return `$${Math.round(n)}`
}

// Percentage delta between two numbers, safe for zero divisor.
function pctDelta(current: number, prev: number): number {
  if (prev <= 0) return current > 0 ? 100 : 0
  return Math.round(((current - prev) / prev) * 100)
}

// ============================================================
// Main
// ============================================================

export default function DashboardPage() {
  const { user } = useAuth()
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dashboardApi.get(),
    refetchInterval: 60_000, // refresh every minute
  })

  const firstName = user?.fullName?.split(" ")[0] ?? "there"
  const { greeting, Icon: GreetingIcon } = getTimeOfDay()

  if (dashboard.isLoading) {
    return (
      <AppShell greetingUser>
        <div className="text-muted-foreground grid place-items-center py-24">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    )
  }

  if (dashboard.error || !dashboard.data) {
    return (
      <AppShell greetingUser>
        <Card className="border-destructive/40 bg-destructive/5 text-destructive p-6">
          Couldn't load dashboard: {(dashboard.error as Error)?.message ?? "Unknown error"}
        </Card>
      </AppShell>
    )
  }

  const { kpi, pipeline, myTasks, activity, settlementsChart, topLenders, topAutomations, myPerformance } =
    dashboard.data

  const perfSettlementsDelta = pctDelta(
    myPerformance.settlementsCount,
    myPerformance.settlementsCountPrev
  )
  const perfValueDelta = pctDelta(
    myPerformance.settlementsValue,
    myPerformance.settlementsValuePrev
  )

  return (
    <AppShell>
      <div className="space-y-6">
        {/* ============================================================ */}
        {/* GREETING HEADER                                              */}
        {/* ============================================================ */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-foreground flex items-center gap-2 text-2xl font-semibold">
              <GreetingIcon className="text-amber-500 h-6 w-6" />
              {greeting}, {firstName}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">{formatDateHuman()}</p>
          </div>
        </div>

        {/* ============================================================ */}
        {/* KPI TILES  (6 across)                                        */}
        {/* ============================================================ */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <KpiTile
            icon={Users}
            label="New Leads"
            value={kpi.newLeads}
            trend={kpi.trendNewLeads}
            trendLabel="vs last 7 days"
            iconTone="blue"
            to="/pipeline?stage=new_lead"
          />
          <KpiTile
            icon={CheckSquare}
            label="Follow-ups Due"
            value={kpi.followUps}
            subtitle={
              kpi.followUpsOverdue > 0
                ? `${kpi.followUpsOverdue} overdue`
                : "on track"
            }
            subtitleTone={kpi.followUpsOverdue > 0 ? "red" : "grey"}
            iconTone="amber"
            to="/tasks"
          />
          <KpiTile
            icon={FileCheck}
            label="Awaiting Docs"
            value={kpi.awaitingDocs}
            subtitle={
              kpi.awaitingDocsOverdue > 0
                ? `${kpi.awaitingDocsOverdue} > 3 days`
                : "on track"
            }
            subtitleTone={kpi.awaitingDocsOverdue > 0 ? "red" : "grey"}
            iconTone="violet"
            to="/pipeline?stage=awaiting_docs"
          />
          <KpiTile
            icon={Send}
            label="Docs Received"
            value={kpi.submitted}
            subtitle={
              kpi.submittedThisWeek > 0
                ? `${kpi.submittedThisWeek} this week`
                : "none this week"
            }
            iconTone="cyan"
            to="/pipeline?stage=docs_received"
          />
          <KpiTile
            icon={CheckCircle2}
            label="Approved"
            value={kpi.approved}
            subtitle={
              kpi.approvedThisWeek > 0
                ? `${kpi.approvedThisWeek} this week`
                : "none this week"
            }
            iconTone="emerald"
            to="/pipeline?stage=approved"
          />
          <KpiTile
            icon={Handshake}
            label="Settled (Month)"
            value={kpi.settledCount}
            subtitle={formatShortCurrency(kpi.settledValueMonth)}
            subtitleTone="emerald"
            iconTone="teal"
            to="/pipeline?stage=settled"
          />
        </div>

        {/* ============================================================ */}
        {/* PIPELINE + SETTLEMENTS CHART                                 */}
        {/* ============================================================ */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Pipeline overview (2/3 wide) */}
          <Card className="p-4 lg:col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">Pipeline Overview</h2>
              <Link
                to="/pipeline"
                className="text-rc-blue-600 hover:text-rc-blue-700 inline-flex items-center gap-1 text-xs font-medium"
              >
                View pipeline
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {OVERVIEW_STAGES.map((s) => {
                const stage = pipeline[s] ?? { count: 0, total: 0, recent: [] }
                return (
                  <Link
                    key={s}
                    to={`/pipeline?stage=${s}`}
                    className={cn(
                      "relative overflow-hidden rounded-md bg-gradient-to-br p-3 text-white transition-transform hover:-translate-y-0.5",
                      STAGE_GRADIENT[s]
                    )}
                  >
                    <div className="text-[10px] font-medium uppercase tracking-wide opacity-90">
                      {STAGE_LABEL[s]}
                    </div>
                    <div className="mt-1 text-2xl font-bold leading-none">{stage.count}</div>
                    <div className="mt-0.5 text-xs opacity-90">
                      {stage.total > 0 ? formatShortCurrency(stage.total) : "—"}
                    </div>
                  </Link>
                )
              })}
            </div>
          </Card>

          {/* Settlements chart (1/3 wide) */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">Settlements</h2>
              <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
                Last 6 months
              </span>
            </div>
            <SettlementsBarChart data={settlementsChart} />
          </Card>
        </div>

        {/* ============================================================ */}
        {/* MY TASKS + RECENT ACTIVITY + AUTOMATIONS                     */}
        {/* ============================================================ */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* My Tasks */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">My Tasks</h2>
              <Link
                to="/tasks"
                className="text-rc-blue-600 hover:text-rc-blue-700 inline-flex items-center gap-1 text-xs font-medium"
              >
                All tasks
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            {myTasks.overdue.length + myTasks.today.length + myTasks.upcoming.length === 0 ? (
              <EmptyState
                icon={CheckSquare}
                message="No open tasks. Nice."
              />
            ) : (
              <ul className="divide-border divide-y">
                {[...myTasks.overdue, ...myTasks.today, ...myTasks.upcoming]
                  .slice(0, 6)
                  .map((t) => {
                    const isOverdue =
                      t.dueAt !== null && t.dueAt < Math.floor(Date.now() / 1000)
                    const label = t.contactFirstName
                      ? `${t.contactFirstName} ${t.contactLastName ?? ""}`.trim()
                      : t.contactBusinessName ?? t.dealName ?? "Unlinked task"
                    return (
                      <li key={t.id} className="flex items-center gap-3 py-2">
                        <div className="flex-1 min-w-0">
                          <Link
                            to={
                              t.contactId
                                ? `/contacts/${t.contactId}`
                                : t.dealId
                                  ? `/deals/${t.dealId}`
                                  : "/tasks"
                            }
                            className="text-foreground hover:text-rc-blue-700 block truncate text-sm font-medium"
                          >
                            {t.title}
                          </Link>
                          <div className="text-muted-foreground truncate text-xs">
                            {label}
                          </div>
                        </div>
                        <TaskDuePill dueAt={t.dueAt} isOverdue={isOverdue} />
                      </li>
                    )
                  })}
              </ul>
            )}
          </Card>

          {/* Recent Activity */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">Recent Activity</h2>
              <Link
                to="/audit"
                className="text-rc-blue-600 hover:text-rc-blue-700 inline-flex items-center gap-1 text-xs font-medium"
              >
                Full log
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            {activity.length === 0 ? (
              <EmptyState icon={Clock} message="Nothing yet." />
            ) : (
              <ul className="space-y-2">
                {activity.slice(0, 6).map((a) => {
                  const Icon = ACTION_ICON[a.action] ?? Clock
                  const label = ACTION_LABEL[a.action] ?? a.action.replace(/_/g, " ")
                  return (
                    <li key={a.id} className="flex items-start gap-2 text-xs">
                      <span className="text-muted-foreground bg-muted mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full">
                        <Icon className="h-3 w-3" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-foreground truncate">
                          <span className="font-medium">{a.userName ?? "System"}</span>{" "}
                          <span className="text-muted-foreground">{label.toLowerCase()}</span>
                        </div>
                        <div className="text-muted-foreground text-[11px]">
                          {timeAgo(a.createdAt)}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>

          {/* Automations */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">Automations</h2>
              <Link
                to="/automations"
                className="text-rc-blue-600 hover:text-rc-blue-700 inline-flex items-center gap-1 text-xs font-medium"
              >
                All
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            {topAutomations.length === 0 ? (
              <EmptyState
                icon={Zap}
                message="No automations set up yet."
                cta={{ to: "/automations", label: "Create one" }}
              />
            ) : (
              <ul className="space-y-2">
                {topAutomations.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/automations/${a.id}`}
                      className="flex items-center justify-between gap-2 rounded-md p-2 -m-2 hover:bg-accent"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-foreground truncate text-sm font-medium">
                          {a.name}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {a.activeCount === 0
                            ? "Nobody enrolled"
                            : `${a.activeCount} enrolled${a.waitingCount > 0 ? ` · ${a.waitingCount} waiting` : ""}`}
                        </div>
                      </div>
                      <AutomationStatusPill status={a.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* ============================================================ */}
        {/* TOP LENDERS + MY PERFORMANCE + QUICK ACTIONS                 */}
        {/* ============================================================ */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Top Lenders */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">Top Lenders</h2>
              <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
                Last 30 days
              </span>
            </div>
            {topLenders.length === 0 ? (
              <EmptyState
                icon={Award}
                message="No settled deals in the last 30 days."
              />
            ) : (
              <ul className="space-y-2.5">
                {topLenders.map((l, idx) => {
                  const max = topLenders[0]?.total ?? 1
                  const pct = max > 0 ? Math.round((l.total / max) * 100) : 0
                  return (
                    <li key={l.lender} className="text-xs">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-muted-foreground w-4 shrink-0">
                          {idx + 1}.
                        </span>
                        <LenderLogo name={l.lender} logoUrl={l.logoUrl} size="sm" />
                        <div className="min-w-0 flex-1 truncate">
                          <span className="text-foreground font-medium">{l.lender}</span>
                        </div>
                        <div className="text-muted-foreground shrink-0 ml-2">
                          {l.count} · {formatShortCurrency(l.total)}
                        </div>
                      </div>
                      <div className="bg-muted h-1.5 overflow-hidden rounded-full ml-6">
                        <div
                          className="bg-rc-blue-500 h-full rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>

          {/* My Performance (MTD) */}
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-foreground text-sm font-semibold">My Performance</h2>
              <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
                MTD
              </span>
            </div>
            <div className="space-y-3">
              <PerfRow
                icon={CheckCircle2}
                label="Settlements"
                value={`${myPerformance.settlementsCount}`}
                delta={perfSettlementsDelta}
                iconTone="emerald"
              />
              <PerfRow
                icon={DollarSign}
                label="Settled Value"
                value={formatShortCurrency(myPerformance.settlementsValue)}
                delta={perfValueDelta}
                iconTone="emerald"
              />
              <PerfRow
                icon={Target}
                label="Conversion Rate"
                value={`${myPerformance.conversionRate}%`}
                progress={myPerformance.conversionRate}
                iconTone="cyan"
              />
              <PerfRow
                icon={Clock}
                label="Avg Settlement Time"
                value={
                  myPerformance.avgSettlementDays > 0
                    ? `${myPerformance.avgSettlementDays}d`
                    : "—"
                }
                iconTone="amber"
              />
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="p-4">
            <h2 className="text-foreground mb-3 text-sm font-semibold">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-2">
              <QuickAction to="/contacts/new" icon={UserPlus} label="New Contact" />
              <QuickAction to="/deals/new" icon={Plus} label="New Deal" />
              <QuickAction to="/tasks" icon={CheckSquare} label="New Task" />
              <QuickAction to="/import" icon={FileText} label="Import" />
              <QuickAction to="/automations" icon={Zap} label="Automations" />
              <QuickAction to="/reports" icon={BarChart3} label="Reports" />
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}

// ============================================================
// KpiTile
// ============================================================

const KPI_ICON_TONES: Record<string, string> = {
  blue: "bg-blue-100 text-blue-700",
  amber: "bg-amber-100 text-amber-700",
  violet: "bg-violet-100 text-violet-700",
  cyan: "bg-cyan-100 text-cyan-700",
  emerald: "bg-emerald-100 text-emerald-700",
  teal: "bg-teal-100 text-teal-700",
}

function KpiTile({
  icon: Icon,
  label,
  value,
  subtitle,
  subtitleTone,
  trend,
  trendLabel,
  iconTone,
  to,
}: {
  icon: typeof Users
  label: string
  value: number
  subtitle?: string
  subtitleTone?: "grey" | "red" | "emerald"
  trend?: number
  trendLabel?: string
  iconTone: string
  to: string
}) {
  const subtitleClass =
    subtitleTone === "red"
      ? "text-red-600"
      : subtitleTone === "emerald"
        ? "text-emerald-600"
        : "text-muted-foreground"
  return (
    <Link to={to}>
      <Card className="hover:border-rc-blue-300 flex flex-col p-3 transition-colors">
        <div className="flex items-start justify-between">
          <span
            className={cn(
              "grid h-8 w-8 place-items-center rounded-md",
              KPI_ICON_TONES[iconTone] ?? "bg-muted"
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          {trend !== undefined && trend !== 0 && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[10px] font-medium",
                trend > 0 ? "text-emerald-600" : "text-red-600"
              )}
            >
              {trend > 0 ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div className="mt-2 text-2xl font-bold leading-none text-foreground">{value}</div>
        <div className="text-muted-foreground mt-1 text-[11px] font-medium uppercase tracking-wide">
          {label}
        </div>
        {subtitle && (
          <div className={cn("mt-1.5 text-[11px]", subtitleClass)}>
            {subtitle}
            {trendLabel && trend !== undefined && (
              <span className="text-muted-foreground ml-1">{trendLabel}</span>
            )}
          </div>
        )}
        {!subtitle && trendLabel && (
          <div className="text-muted-foreground mt-1.5 text-[11px]">{trendLabel}</div>
        )}
      </Card>
    </Link>
  )
}

// ============================================================
// SettlementsBarChart — inline SVG so we don't pull in a chart library
// ============================================================

function SettlementsBarChart({
  data,
}: {
  data: Array<{ month: string; label: string; count: number; total: number }>
}) {
  const max = Math.max(...data.map((d) => d.total), 1)
  const hasData = data.some((d) => d.total > 0)

  if (!hasData) {
    return (
      <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
        No settlements yet
      </div>
    )
  }

  return (
    <div className="h-32">
      <div className="flex h-full items-end justify-between gap-1.5">
        {data.map((d) => {
          const heightPct = max > 0 ? (d.total / max) * 100 : 0
          return (
            <div
              key={d.month}
              className="flex flex-1 flex-col items-center justify-end gap-1"
            >
              <div
                className="w-full rounded-t-sm bg-gradient-to-b from-emerald-500 to-emerald-600 transition-all hover:from-emerald-400"
                style={{ height: `${heightPct}%`, minHeight: d.total > 0 ? 6 : 2 }}
                title={`${d.label}: ${d.count} settlements · ${formatShortCurrency(d.total)}`}
              />
              <div className="text-muted-foreground text-[10px]">{d.label}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================
// AutomationStatusPill
// ============================================================

function AutomationStatusPill({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: "Running", cls: "bg-emerald-100 text-emerald-700" },
    paused: { label: "Paused", cls: "bg-amber-100 text-amber-800" },
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
    archived: { label: "Archived", cls: "bg-rc-grey-100 text-rc-grey-700" },
  }
  const s = map[status] ?? { label: status, cls: "bg-rc-grey-100 text-rc-grey-700" }
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0", s.cls)}>
      {s.label}
    </span>
  )
}

// ============================================================
// TaskDuePill
// ============================================================

function TaskDuePill({
  dueAt,
  isOverdue,
}: {
  dueAt: number | null
  isOverdue: boolean
}) {
  if (!dueAt) {
    return (
      <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-rc-grey-100 text-rc-grey-700 shrink-0">
        No date
      </span>
    )
  }
  const due = new Date(dueAt * 1000)
  // Compute "today" vs "tomorrow" using Brisbane calendar days, not the
  // browser's local tz. Brisbane is fixed UTC+10, so any instant's Brisbane
  // day-index = floor((epochMs + 10h) / 24h).
  const BRIS_OFFSET_MS = 10 * 60 * 60 * 1000
  const todayIdx = Math.floor((Date.now() + BRIS_OFFSET_MS) / (24 * 60 * 60 * 1000))
  const dueIdx = Math.floor((dueAt * 1000 + BRIS_OFFSET_MS) / (24 * 60 * 60 * 1000))
  const daysDiff = dueIdx - todayIdx

  let label: string
  let cls: string
  if (isOverdue) {
    label = "Overdue"
    cls = "bg-red-100 text-red-700"
  } else if (daysDiff === 0) {
    label = "Today"
    cls = "bg-amber-100 text-amber-800"
  } else if (daysDiff === 1) {
    label = "Tomorrow"
    cls = "bg-blue-100 text-blue-700"
  } else if (daysDiff > 0 && daysDiff <= 7) {
    label = due.toLocaleDateString("en-AU", { weekday: "short", timeZone: "Australia/Brisbane" })
    cls = "bg-cyan-100 text-cyan-700"
  } else {
    label = due.toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: "Australia/Brisbane" })
    cls = "bg-slate-100 text-slate-700"
  }
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0", cls)}>
      {label}
    </span>
  )
}

// ============================================================
// PerfRow
// ============================================================

const PERF_ICON_TONES: Record<string, string> = {
  emerald: "text-emerald-600",
  cyan: "text-cyan-600",
  amber: "text-amber-600",
}

function PerfRow({
  icon: Icon,
  label,
  value,
  delta,
  progress,
  iconTone,
}: {
  icon: typeof Users
  label: string
  value: string
  delta?: number
  progress?: number
  iconTone: string
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-4 w-4", PERF_ICON_TONES[iconTone] ?? "text-muted-foreground")} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-foreground text-sm font-semibold">{value}</span>
          {delta !== undefined && delta !== 0 && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[10px] font-medium",
                delta > 0 ? "text-emerald-600" : "text-red-600"
              )}
            >
              {delta > 0 ? (
                <TrendingUp className="h-2.5 w-2.5" />
              ) : (
                <TrendingDown className="h-2.5 w-2.5" />
              )}
              {Math.abs(delta)}%
            </span>
          )}
        </div>
      </div>
      {progress !== undefined && (
        <div className="bg-muted h-1.5 overflow-hidden rounded-full">
          <div
            className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-full rounded-full transition-all"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}
    </div>
  )
}

// ============================================================
// QuickAction
// ============================================================

function QuickAction({
  to,
  icon: Icon,
  label,
}: {
  to: string
  icon: typeof Users
  label: string
}) {
  return (
    <Link
      to={to}
      className="hover:border-rc-blue-300 hover:bg-rc-blue-50/40 flex flex-col items-center gap-1.5 rounded-md border border-border p-3 text-center transition-colors"
    >
      <Icon className="text-rc-blue-600 h-5 w-5" />
      <span className="text-foreground text-xs font-medium">{label}</span>
    </Link>
  )
}

// ============================================================
// EmptyState
// ============================================================

function EmptyState({
  icon: Icon,
  message,
  cta,
}: {
  icon: typeof Users
  message: string
  cta?: { to: string; label: string }
}) {
  return (
    <div className="flex flex-col items-center py-6 text-center">
      <Icon className="text-muted-foreground mb-2 h-6 w-6 opacity-50" />
      <p className="text-muted-foreground text-xs">{message}</p>
      {cta && (
        <Link
          to={cta.to}
          className="text-rc-blue-600 hover:text-rc-blue-700 mt-2 text-xs font-medium"
        >
          {cta.label}
        </Link>
      )}
    </div>
  )
}
